import React from "react";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { useQueryClient } from "@tanstack/react-query";
import { QuestionnaireService } from "./questionnaireService";
import { QuestionnaireForm } from "./questionnaireForm";
import { QuestionnairePage } from "./questionnairePage";
import { FormDto, QuestionDto } from "../../../services/dto";
import { QuestionnaireView } from "./questionnaireView";
import { Dialog, LoadingIndicator } from "../../../assets/vfx";
import { QuestionForm } from "../question/questionForm";
import { QuestionService } from "../question/questionService";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";
import { IActionResponse } from "../../../services/proxies";

export enum QuestionnaireType {
  Poll = "9b140eb7-7647-f011-8b7a-cc1531973717",
  Event_RSVP = "9c140eb7-7647-f011-8b7a-cc1531973717",
  Survey = "9d140eb7-7647-f011-8b7a-cc1531973717",
  Feedback = "8f3217e9-0f6f-f011-8ef6-00155d112802"
}

export enum QuestionnaireStatus {
    Active = "015b428b-8d5b-40ad-707a-08dd687a96f6",
    Scheduled = "8aa6edc4-00b4-4e3a-7079-08dd687a96f6",
    Completed = "cf86ff5b-5f9e-4fd2-707b-08dd687a96f6",
    Declined = "60113f2f-9d41-4d55-707c-08dd687a96f6",
    Closed = "574df843-e256-4c74-707d-08dd687a96f6",
}

export enum QuestionnaireNotificationType {
    poll = "14d8b1d8-cada-f011-907a-000d3a153cf8",
    feedback = "50a4f789-cbda-f011-8ef8-00155d112802",
    survey = "6acfe498-2bdb-f011-8b96-cc1531973717"
}


export interface questionnaireContainerProps {
  questionnaireType?: QuestionnaireType | undefined;
}

export const QuestionnaireContainer = (props: questionnaireContainerProps) => {
  const queryClient = useQueryClient();
  const [data, setData] = React.useState<FormDto>();
  const [showQuestionForm, setShowQuestionForm] = React.useState<boolean>(false);
  const [drawerType, setDrawerType] = React.useState<GridActionType[]>([
    GRID_ACTION_TYPES.HIDDEN,
  ]);
    const [showDialog, setShowDialog] = React.useState<boolean>(false);

    const lookupQuery = QuestionnaireService.getLookups();
    const sectionLookupQuery = QuestionService.getLookups(data?.id)

  const { showSnackbar } = useSnackbar();
  const questionnaireQuery = QuestionnaireService.getQuestionnaireQuery(
    data?.id ?? Constants.emptyGuid
  );

  const questionnaireResponseQuery = QuestionnaireService.getQuestionnaireResponseQuery(
    data?.id ?? Constants.emptyGuid
  );

  const { mutateAsync: saveQuestionnaire } = QuestionnaireService.save();
  const { mutateAsync: saveQuestionnaireQuestion } = QuestionService.save();
  const { mutate: deActivateQuestionnaire } =
    QuestionnaireService.deActivateQuestionnaire();
  const { mutate: deleteQuestionnaire } =
        QuestionnaireService.deleteQuestionnaire();

  const handleSubmit = async (values: FormDto) => {
    const isEdit = data && data.id && data.id !== Constants.emptyGuid;
    values.id = isEdit ? data.id : Constants.emptyGuid;
      values.statusId = !values.statusId || values.statusId == Constants.emptyGuid ? QuestionnaireStatus.Scheduled : values.statusId;
    return await saveQuestionnaire(values, {
        onSuccess: () => {
        const message: string = props.questionnaireType == QuestionnaireType.Poll.toLowerCase() ? "Poll Saved" : props.questionnaireType == QuestionnaireType.Feedback.toLowerCase() ? "Feedback Saved" : "Questionnaire Saved";
        showSnackbar(message, MessageSeverity.Success);
        queryClient.invalidateQueries({ queryKey: ["questionnaireGridQuery"] });
        questionnaireQuery.refetch();
        closeDrawer();
      },
    });
  };

    const handleQuestionSubmit = async (values: QuestionDto) => {
        const isEdit = data && data.id && data.id !== Constants.emptyGuid;
        values.id = isEdit ? values.id : Constants.emptyGuid;
        const message = isEdit ? "Question updated" : "Question saved";
        return await saveQuestionnaireQuestion(values, {
            onSuccess: (response) => {
                const errorMessage = apiErrorMessage(response.errorMessages);
                if (errorMessage) {
                    showSnackbar(errorMessage, MessageSeverity.Error);
                } else {
                    showSnackbar(message, MessageSeverity.Success);
                    queryClient.invalidateQueries({
                        queryKey: ["questionnaireQuery"],
                    });
                    closeQuestionForm();
                }
            },
        });
    };

  const handleCustomEvent = (actionType: string, dataId: string) => {
    if (isGridActionType(actionType)) {
      setDrawerType([actionType]);
      setData({ id: dataId } as FormDto);
      setSearchAction(actionType, dataId);
    }
      if (actionType == "delete") {
          setData(({ id: dataId } as FormDto));
          setShowDialog(true);
      }
  };

    const handleDelete = () => deleteQuestionnaire(data?.id ?? Constants.emptyGuid,
        {
            onSuccess: (response: IActionResponse) => {
                showSnackbar("Questionnaire deleted successfully", MessageSeverity.Success);
                queryClient.invalidateQueries({ queryKey: ["questionnaireGridQuery"] });
                questionnaireQuery.refetch();
            }
        });

    const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);
    
  const closeDrawer = (drawertype?: GridActionType) => {
    if (drawertype) {
      const drawerArray = [...drawerType];
      const index = drawerArray.indexOf(drawertype);
      if (index != -1) {
        drawerArray.splice(index, 1);
        setDrawerType(drawerArray);
      } else {
        setDrawerType([GRID_ACTION_TYPES.HIDDEN]);
      }
    } else {
      setDrawerType([GRID_ACTION_TYPES.HIDDEN]);
      }
      clearSearchAction();
  };

  function EditRecord(data: any, showView: boolean | undefined) {
    setData(data);
    const drawerTypes: GridActionType[] = [];
    if (showView) {
      drawerTypes.push(GRID_ACTION_TYPES.VIEW);
    }
    drawerTypes.push(GRID_ACTION_TYPES.EDIT);
    setDrawerType(drawerTypes);
  }

    function EditQuestion(data: any, showView: boolean) {
        queryClient.invalidateQueries({ queryKey: ["questionLookupsQuery"] });
        setShowQuestionForm(showView);
    }

    const closeQuestionForm = () => {
        setShowQuestionForm(false);
    }

  const handleDeactivate = () => {
    deActivateQuestionnaire(data?.id ?? Constants.emptyGuid, {
      onSuccess: () => {
        showSnackbar("Form Deactivated", MessageSeverity.Success);
        queryClient.invalidateQueries({ queryKey: ["questionnaireGridQuery"] });
        closeDrawer();
      },
    });
  };

  const renderDrawerContent = () => {
    const isDataLoaded = !!questionnaireQuery?.data && !!lookupQuery?.data;
    return drawerType.map((i: GridActionType, index: number) => {
      switch (i) {
        case GRID_ACTION_TYPES.NEW: {
          return (
            !!lookupQuery?.data && (
              <QuestionnaireForm
                show={true}
                onHide={() => closeDrawer(GRID_ACTION_TYPES.NEW)}
                onSave={(data: FormDto) => handleSubmit(data)}
                lookups={lookupQuery?.data}
                key={index}
                questionnaireType={props.questionnaireType}
              />
            )
          );
        }
        case GRID_ACTION_TYPES.EDIT: {
          return isDataLoaded ? (
            <QuestionnaireForm
              data={questionnaireQuery?.data}
              show={!!questionnaireQuery?.data}
              onHide={() => closeDrawer(GRID_ACTION_TYPES.EDIT)}
              onSave={(data: FormDto) => handleSubmit(data)}
              lookups={lookupQuery?.data}
              key={index}
              questionnaireType={props.questionnaireType}
            />
          ) : null;
        }
        case GRID_ACTION_TYPES.VIEW:
        case GRID_ACTION_TYPES.DOUBLE_CLICK: {
          return isDataLoaded
            ? !!lookupQuery?.data && (
                <QuestionnaireView
                  data={questionnaireQuery?.data}
                  questionnaireResponseData={questionnaireResponseQuery?.data}
                  show={!!questionnaireQuery?.data}
                  onHide={() => closeDrawer(GRID_ACTION_TYPES.VIEW)}
                  onDeactivate={handleDeactivate}
                  onEdit={() => EditRecord(questionnaireQuery.data, true)}
                  lookups={lookupQuery?.data}
                  key={index}
                  questionnaireType={props.questionnaireType}
                  onQuestionEdit={()=> EditQuestion(questionnaireQuery.data.pollQuestion, true)}
                />
              )
            : null;
        }
      }
    });
  };

  const renderQuestionForm = () => {
      const questionTypeOptions =
          props.questionnaireType === QuestionnaireType.Poll.toLowerCase()
              ? lookupQuery.data?.pollQuestionTypes ?? lookupQuery.data?.questionTypes ?? []
              : props.questionnaireType === QuestionnaireType.Survey.toLowerCase()
                  ? lookupQuery.data?.surveyQuestionTypes ?? lookupQuery.data?.questionTypes ?? []
                  : lookupQuery.data?.questionTypes ?? [];

      return (
          questionnaireQuery.data?.pollQuestion &&
          <QuestionForm
          data={questionnaireQuery.data?.pollQuestion}
          show={showQuestionForm}
          onHide={closeQuestionForm}
          onSave={handleQuestionSubmit}
          lookups={{ questionType: questionTypeOptions, sections: sectionLookupQuery.data?.sections ?? [] }}
          questionnaireType={props.questionnaireType}
      />
     )
  }

  return (
    <div>
      {lookupQuery?.isLoading ? (
        <LoadingIndicator isFullPage={true} />
      ) : (
        <QuestionnairePage
          onCustomEvent={handleCustomEvent}
          lookups={lookupQuery?.data}
          questionnaireType={props.questionnaireType}
        />
      )}
          {renderDrawerContent()}
          {renderQuestionForm()}
          {
              <Dialog
                  contentText={"Are you sure you want to delete?"}
                  contentTitle={"Delete"}
                  onClose={() => setShowDialog(false)}
                  onConfirm={() => {
                      setShowDialog(false);
                      handleDelete();
                  }}
                  open={showDialog}
                  closeText={"No, Go Back"}
                  confirmText={"Yes, Continue"}
              />
          }
    </div>
  );
};
